import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { SiteNav } from '@/lib/content'
import type { Faq, Service } from '@/types'

import { useEffect, useState } from 'react'
import { Clock, Mail, MapPin, Phone } from 'lucide-react'

import { pageSeo } from '@/constants/page-seo'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { JsonLd } from '@/components/generals/json-ld'
import { FaqBlock } from '@/components/generals/faq-block'
import { PageHeader } from '@/components/generals/page-header'
import { Reveal } from '@/components/generals/reveal'
import { EnquiryForm } from '@/components/forms/enquiry-form'
import { Container } from '@/components/generals/primitives'
import {
    breadcrumbSchema,
    contactPageSchema,
    faqSchema,
    organisationSchema
} from '@/lib/schema'
import { contact } from '@/constants/site'
import { getFaqs, getServices, getSiteNav } from '@/lib/content'

interface ContactPageProps {
    nav: SiteNav
    services: Service[]
    faqs: Faq[]
}

const PAGE_TITLE = 'Request a quotation'
const PAGE_DESCRIPTION = 'Request a fixed written quotation for an extension, loft conversion or structural work across South-East London and Kent. Free site visit, reply within one working day.'

const nextSteps = [
    'We read the enquiry and reply within one working day.',
    'If it looks like a fit, we offer two dates for a free site visit.',
    'We measure up, check the drains, the roof line and the party walls.',
    'You get a fixed written quotation with a programme and a start date.'
]

const ContactPage: FC<ContactPageProps> = ({
    nav = { services: [], areas: [] },
    services = [],
    faqs = []
}): ReactNode => {
    const [notice, setNotice] = useState<'sent' | 'error' | null>(null)
    const faqData = faqSchema(faqs)

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)

        if (params.get('sent')) setNotice('sent')
        else if (params.get('error')) setNotice('error')
    }, [])

    return (
        <Layout nav={nav}>
            <Seo
                fullTitle={pageSeo.contact.title}
                description={pageSeo.contact.description}
                path='/contact'
            />

            <JsonLd data={organisationSchema({ services, areas: nav.areas })} />
            <JsonLd
                data={contactPageSchema({
                    headline: PAGE_TITLE,
                    description: PAGE_DESCRIPTION
                })}
            />
            {faqData ? <JsonLd data={faqData} /> : null}
            <JsonLd
                data={breadcrumbSchema([
                    { name: 'Home', path: '/' },
                    { name: 'Contact', path: '/contact' }
                ])}
            />

            <PageHeader
                eyebrow='Get in touch'
                title={PAGE_TITLE}
                lead='Tell us the address, the property and roughly what you are trying to achieve. That is enough for us to tell you whether it is worth a visit.'
                crumbs={[
                    { name: 'Home', href: '/' },
                    { name: 'Contact', href: '/contact' }
                ]}
                meta={
                    <ul className='space-y-4 border-t border-ink-line pt-6 text-sm'>
                        <li>
                            <a
                                href={contact.phoneHref}
                                className='flex items-center gap-3 font-semibold text-paper transition-colors hover:text-minium-tint'
                            >
                                <Phone aria-hidden='true' className='size-4 shrink-0 text-minium' />
                                {contact.phone}
                            </a>
                        </li>

                        <li>
                            <a
                                href={contact.emailHref}
                                className='flex items-center gap-3 break-all text-paper/85 transition-colors hover:text-minium-tint'
                            >
                                <Mail aria-hidden='true' className='size-4 shrink-0 text-minium' />
                                {contact.email}
                            </a>
                        </li>
                    </ul>
                }
            />

            <section className='bg-paper py-16 lg:py-24'>
                <Container>
                    {notice ? (
                        <div
                            role='status'
                            className={
                                notice === 'sent'
                                    ? 'mb-12 border-l-2 border-minium bg-paper-raised px-6 py-5'
                                    : 'mb-12 border-l-2 border-destructive bg-paper-raised px-6 py-5'
                            }
                        >
                            <p className='text-sm text-ink'>
                                {notice === 'sent'
                                    ? 'Thank you, your enquiry has reached us. We will reply within one working day.'
                                    : 'Something in that submission did not go through. Please check the fields and try again, or call us instead.'}
                            </p>
                        </div>
                    ) : null}

                    <div className='grid gap-14 lg:grid-cols-12 lg:gap-16'>
                        <div className='lg:col-span-7'>
                            <EnquiryForm services={services} sourcePage='/contact' />
                        </div>

                        <aside className='lg:col-span-4 lg:col-start-9'>
                            <Reveal delay={100}>
                                <div className='border-t-2 border-ink pt-6'>
                                    <h2 className='font-mono text-[0.65rem] uppercase tracking-label text-muted-foreground'>
                                        What happens next
                                    </h2>

                                    <ol className='mt-6 space-y-5'>
                                        {nextSteps.map((step, index) => (
                                            <li key={step} className='flex gap-4'>
                                                <span className='font-mono text-[0.7rem] text-minium'>
                                                    {String(index + 1).padStart(2, '0')}
                                                </span>
                                                <span className='text-sm leading-relaxed text-ink'>
                                                    {step}
                                                </span>
                                            </li>
                                        ))}
                                    </ol>
                                </div>

                                <div className='mt-10 border-t border-rule pt-6'>
                                    <h2 className='font-mono text-[0.65rem] uppercase tracking-label text-muted-foreground'>
                                        Office hours
                                    </h2>

                                    <dl className='mt-5 space-y-3 text-sm'>
                                        {contact.hours.map((entry) => (
                                            <div key={entry.days} className='flex justify-between gap-4'>
                                                <dt className='text-muted-foreground'>{entry.days}</dt>
                                                <dd className='font-medium text-ink'>{entry.time}</dd>
                                            </div>
                                        ))}
                                    </dl>

                                    <p className='mt-6 flex items-start gap-3 text-sm text-muted-foreground'>
                                        <Clock aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-minium' />
                                        Site teams start at 08:00 and finish by 17:00, so noisy work
                                        stays inside neighbourly hours.
                                    </p>

                                    <p className='mt-4 flex items-start gap-3 text-sm text-muted-foreground'>
                                        <MapPin aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-minium' />
                                        Based in {contact.locality}, working across {contact.coverage}.
                                    </p>
                                </div>
                            </Reveal>
                        </aside>
                    </div>
                </Container>
            </section>

            <FaqBlock
                faqs={faqs}
                eyebrow='Before you ask'
                title='Questions we get every week'
            />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<ContactPageProps> = async () => {
    const [nav, services, faqs] = await Promise.all([
        getSiteNav(),
        getServices(),
        getFaqs('general')
    ])

    return {
        props: { nav, services, faqs },
        revalidate: 60
    }
}

export default ContactPage
